import React, {useState, useEffect} from "react";
import {
  useSessionsRealtime,
  useTitansRealtime,
  useUsersRealtime,
} from "../../hooks/useFirestore";
import {useCharacterInventory} from "../../hooks/useInventory";
import {useAuth} from "../../hooks/useAuth";
import {useNotifications} from "../../components/NotificationSystem";
import {SessionService} from "../../services/api/sessionService";
import {
  getDisplayDate,
  getDisplayTime,
  convertToDate,
  isSessionDone,
} from "../../utils/dateUtils";
import {useLocation, useNavigate} from "react-router-dom";
import buymeacoffeeIcon from "../../assets/buymeacoffee-icon.svg";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import "../../styles/env.css";
import SessionCard from "../../components/SessionCard";
import SessionListItem from "../../components/SessionListItem";
import CharacterSelectionModal from "../../components/CharacterSelectionModal";
import InventoryForm from "../../components/InventoryForm";
import FilterableList from "../../components/FilterableList";
import {BaseFilters} from "../../components/ListFilters";
import RichTextRenderer from "../../components/RichText/RichTextRenderer";

// Types for session and titan (for future use)
// Use the Session type from dummySessions.ts
// Remove local Session type definition if present

type State = "empty" | "filled" | "log";

// Inventory item interface for session logging
interface SessionInventoryItem {
  name: string;
  quantity: number;
  description: string;
  attunement: boolean;
}

// Session filters interface extending base filters
interface SessionFilters extends BaseFilters {
  status: "all" | "available" | "upcoming" | "completed";
  titanId: string;
  minLevel: number;
  maxLevel: number;
}

const Sessions: React.FC = () => {
  const {
    data: firestoreSessions,
    loading: sessionsLoading,
    error: sessionsError,
  } = useSessionsRealtime();
  const {data: titans, loading: titansLoading} = useTitansRealtime();
  const {data: users, loading: usersLoading} = useUsersRealtime();
  const {user} = useAuth();
  const {addNotification} = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();

  // For demo: switch between states
  const [state, setState] = useState<State>("filled");
  // For demo: select a session to log
  const [selectedSession, setSelectedSession] = useState<any | null>(null); // Changed to any to match dummySessions
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Add state for log fields
  // Update log state to match dummySessions structure
  const [logEvents, setLogEvents] = useState("");
  const [logCurrency, setLogCurrency] = useState({
    platinum: "",
    gold: "",
    electrum: "",
    silver: "",
    copper: "",
  });
  const [logItems, setLogItems] = useState<SessionInventoryItem[]>([]);

  // Character selection modal state
  const [showCharacterModal, setShowCharacterModal] = useState(false);
  const [pendingSession, setPendingSession] = useState<any>(null);

  // Track character used for each session
  const [sessionCharacters, setSessionCharacters] = useState<
    Record<string, string>
  >({});

  // Track session log status
  const [sessionLogStatus, setSessionLogStatus] = useState<
    Record<string, boolean>
  >({});

  // Track whether we're editing an existing log
  const [isEditingExistingLog, setIsEditingExistingLog] = useState(false);
  const [copiedOpen, setCopiedOpen] = useState(false);

  const handleCopyCalendarLink = (sessionId: string | undefined) => {
    if (!sessionId) return;
    const url = `${window.location.origin}/calendar?sessionId=${sessionId}`;
    navigator.clipboard.writeText(url).then(() => setCopiedOpen(true));
  };

  // Session filters state - Default to showing only future sessions
  const [filters, setFilters] = useState<SessionFilters>({
    search: "",
    status: "upcoming", // Changed from "available" to "upcoming" to show future sessions by default
    titanId: "",
    minLevel: 1,
    maxLevel: 20,
  });

  // Clear all filters - Reset to default which shows future sessions
  const clearFilters = () => {
    setFilters({
      search: "",
      status: "upcoming", // Reset to upcoming (future sessions) instead of "available"
      titanId: "",
      minLevel: 1,
      maxLevel: 20,
    });
  };

  // Handle URL parameters for opening signup modal
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const sessionId = searchParams.get("sessionId");
    const openSignup = searchParams.get("openSignup");

    if (sessionId && openSignup === "true" && firestoreSessions.length > 0) {
      const session = firestoreSessions.find((s) => s.id === sessionId);
      if (session) {
        // Expand the session and show signup modal
        setExpandedSessionId(sessionId);
        setPendingSession(session);
        setShowCharacterModal(true);

        // Clear the URL parameters
        navigate("/sessions", {replace: true});
      }
    }
  }, [location.search, firestoreSessions, navigate]);

  // Handle URL parameters for opening log form directly
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const sessionId = searchParams.get("sessionId");
    const openLog = searchParams.get("openLog");

    if (
      sessionId &&
      openLog === "true" &&
      firestoreSessions.length > 0 &&
      user?.uid
    ) {
      const session = firestoreSessions.find((s) => s.id === sessionId);
      if (session) {
        (async () => {
          // Open the log form for this session
          await handleLog(session);
          // Clear the URL parameters to avoid re-triggering only after opening
          navigate("/sessions", {replace: true});
        })();
      }
    }
  }, [location.search, firestoreSessions, user?.uid, navigate]);

  // Function to resolve titan ID to titan name or get custom world name
  const getTitanName = (session: any): string => {
    // If there's a custom world name, use it
    if (session.customWorldName && session.customWorldName.trim()) {
      return session.customWorldName;
    }

    // If there's a titanId, try to resolve it
    if (session.titanId && session.titanId.trim()) {
      if (!titans || titans.length === 0) {
        return session.titanId; // Return ID if titans aren't loaded
      }
      const titan = titans.find((t) => t.id === session.titanId);
      return titan ? titan.name : session.titanId; // Fallback to ID if not found
    }

    // Fallback
    return "Unknown World";
  };

  // Helper function to get DM name by ID
  const getDMName = (dmId: string): string => {
    if (!users || users.length === 0) {
      return "DM TBD";
    }
    const dm = users.find((u) => u.uid === dmId);
    return dm ? dm.displayName : "DM TBD";
  };

  // Populate sessionCharacters from actual session data when sessions load
  useEffect(() => {
    if (firestoreSessions && user?.uid) {
      const mapping: Record<string, string> = {};

      for (const session of firestoreSessions) {
        // Find the current user in the session players
        const playerEntry = session.players?.find(
          (p) => p.playerId === user.uid
        );
        if (playerEntry) {
          mapping[session.id!] = playerEntry.characterId;
        }
      }

      setSessionCharacters(mapping);
    }
  }, [firestoreSessions, user?.uid]);

  // Inventory hook for the selected character (when logging)
  const selectedCharacterId = selectedSession
    ? sessionCharacters[selectedSession.id]
    : null;
  const inventory = useCharacterInventory(selectedCharacterId || "");

  // Helper: is user signed up for a session?
  const isSignedUp = (session: any) => {
    if (!user?.uid) return false;
    return (
      session.players &&
      session.players.some((p: any) => p.playerId === user.uid)
    );
  };

  // Helper: is session full?
  const isSessionFull = (session: any) => {
    return (session.players?.length || 0) >= (session.maxPlayers || 6);
  };

  // Helper: can user log this session?
  const canLogSession = (session: any) => {
    if (!user?.uid) return false;

    const isCompleted = session.status === "completed" || isSessionDone(session.date);

    if (!isCompleted) return false;

    // Check if user participated in this session
    const playerInSession = session.players?.find(
      (p: any) => p.playerId === user.uid
    );
    if (!playerInSession) return false;

    // Check if session has already been logged for this character
    const characterId = playerInSession.characterId;
    const sessionKey = `${session.id}_${characterId}`;

    // If we haven't checked this session yet, return false for now
    // The actual check will be done when the component loads
    return sessionLogStatus[sessionKey] === false;
  };

  // Check session log status for completed sessions
  useEffect(() => {
    const checkSessionLogStatus = async () => {
      if (!user?.uid || !firestoreSessions) return;

      const statusChecks: Record<string, boolean> = {};

      for (const session of firestoreSessions) {
        const isCompleted =
          session.status === "completed" || isSessionDone(session.date);

        if (isCompleted) {
          const playerInSession = session.players?.find(
            (p: any) => p.playerId === user.uid
          );

          if (playerInSession) {
            try {
              const result = await SessionService.checkSessionLogStatus({
                sessionId: session.id!,
                playerId: user.uid,
                characterId: playerInSession.characterId,
              });

              const sessionKey = `${session.id}_${playerInSession.characterId}`;
              statusChecks[sessionKey] = result.hasLogged;
            } catch (error) {
              console.error(
                `checkSessionLogStatus - error checking session ${session.id}:`,
                error
              );
              // Error checking session log status - default to false
              const sessionKey = `${session.id}_${playerInSession.characterId}`;
              statusChecks[sessionKey] = false;
            }
          }
        }
      }
      setSessionLogStatus(statusChecks);
    };

    checkSessionLogStatus();
  }, [user?.uid, firestoreSessions]);

  // When opening a log, pre-fill fields if editing
  // Note: This useEffect is now disabled because we handle form population
  // manually in the handleLog function to avoid race conditions
  /*
  useEffect(() => {
    if (state === "log" && selectedSession && selectedSession.sessionLog) {
      setLogEvents(selectedSession.sessionLog.events || "");
      setLogGold(selectedSession.sessionLog.gold || "");
      setLogItems(
        selectedSession.sessionLog.items &&
          selectedSession.sessionLog.items.length > 0
          ? selectedSession.sessionLog.items.map((item: any) => ({
              name: item.name,
              quantity: item.quantity,
              description: item.description,
              attunement: item.attunement,
            }))
          : []
      );
    } else if (state === "log" && selectedSession) {
      setLogEvents("");
      setLogGold("");
      setLogItems([]);
    }
  }, [state, selectedSession]);
  */

  // Combined session filtering logic
  const filteredSessions = firestoreSessions.filter((session) => {

    // When filtering for available sessions
    if (filters.status === "available") {
      // Show only future sessions that are available for signup
      if (session.status !== "available" || isSessionDone(session.date)){
        return false
      }
    }
    // When filtering for upcoming sessions
    else if  (filters.status === "upcoming") {
      // Show all future sessions regardless of status or signup
      if (isSessionDone(session.date)) return false;
    }
    // When filtering for completed sessions
    else if (filters.status === "completed") {
      // Show only completed sessions that the player had signed up for
      if (
        !(session.status === "completed" || isSessionDone(session.date)) ||
        !isSignedUp(session)
      )
        return false;
    }

    // Titan filter
    if (filters.titanId && session.titanId !== filters.titanId) return false;

    // Level range filter
    const sessionLevelRange = session.levelRange || "Level 1-20";
    const levelMatch = sessionLevelRange.match(/Level (\d+)-(\d+)/);
    if (levelMatch) {
      const sessionMinLevel = parseInt(levelMatch[1], 10);
      const sessionMaxLevel = parseInt(levelMatch[2], 10);

      // Check if session level range overlaps with filter range
      if (
        sessionMaxLevel < filters.minLevel ||
        sessionMinLevel > filters.maxLevel
      )
        return false;
    }

    return true;
  });

  // Sort sessions by date - next upcoming session first, then further in the future
  const sortedSessions = [...filteredSessions].sort((a, b) => {
    const dateA = convertToDate(a.date);
    const dateB = convertToDate(b.date);
    return dateA.getTime() - dateB.getTime(); // Ascending order (earliest first)
  });

  // Handler: sign up for session
  const handleSignUp = async (session: any) => {
    if (!user?.uid) {
      addNotification({
        type: "error",
        title: "Authentication Required",
        message: "You must be logged in to sign up for sessions.",
      });
      return;
    }

    // Show character selection modal
    setPendingSession(session);
    setShowCharacterModal(true);
  };

  // Handler: character selected for session
  const handleCharacterSelected = async (characterId: string) => {
    if (!pendingSession || !user?.uid) return;

    setIsSubmitting(true);
    try {
      await SessionService.joinSession({
        sessionId: pendingSession.id,
        playerId: user.uid,
        characterId: characterId,
      });

      // Store the character ID for this session
      setSessionCharacters((prev) => ({
        ...prev,
        [pendingSession.id]: characterId,
      }));

      addNotification({
        type: "success",
        title: "Session Joined",
        message: `Successfully joined "${pendingSession.name}"`,
      });
    } catch (error: any) {
      // Error joining session - handled by notification system
      const errorMessage =
        error.message || "Failed to join session. Please try again.";
      addNotification({
        type: "error",
        title: "Join Failed",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
      setPendingSession(null);
    }
  };

  // Handler: withdraw from session
  const handleWithdraw = async (session: any) => {
    if (!user?.uid) {
      addNotification({
        type: "error",
        title: "Authentication Required",
        message: "You must be logged in to withdraw from sessions.",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await SessionService.leaveSession({
        sessionId: session.id,
        playerId: user.uid,
      });

      addNotification({
        type: "success",
        title: "Session Left",
        message: `Successfully withdrew from "${session.name}"`,
      });
    } catch (error: any) {
      // Error leaving session - handled by notification system
      const errorMessage =
        error.message || "Failed to leave session. Please try again.";
      addNotification({
        type: "error",
        title: "Withdraw Failed",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler: view/edit log
  const handleLog = async (session: any) => {
    if (!user?.uid) return;

    // Get the character ID for this session
    const playerInSession = session.players?.find(
      (p: any) => p.playerId === user.uid
    );

    if (!playerInSession) {
      addNotification({
        type: "error",
        title: "Not Found",
        message: "You were not found as a participant in this session.",
      });
      return;
    }

    const characterId = playerInSession.characterId;
    const sessionKey = `${session.id}_${characterId}`;

    // Always check the current log status directly from the server
    // instead of relying on the cached sessionLogStatus state
    try {
      const statusResult = await SessionService.checkSessionLogStatus({
        sessionId: session.id,
        playerId: user.uid,
        characterId: characterId,
      });

      const hasLogged = statusResult.hasLogged;

      // If they have already logged, fetch the existing log data
      if (hasLogged) {
        try {
          const result = await SessionService.getSessionLog({
            sessionId: session.id,
            playerId: user.uid,
            characterId: characterId,
          });

          // Pre-fill the form with existing log data
          setLogEvents(result.logData.events || "");
          setLogCurrency({
            platinum: (result.logData.currency?.platinum || 0).toString(),
            gold: (result.logData.currency?.gold || 0).toString(),
            electrum: (result.logData.currency?.electrum || 0).toString(),
            silver: (result.logData.currency?.silver || 0).toString(),
            copper: (result.logData.currency?.copper || 0).toString(),
          });
          setLogItems(
            result.logData.items?.map((item: any) => ({
              name: item.name,
              quantity: item.quantity,
              description: item.description,
              attunement: item.attunement || false,
            })) || []
          );
          setIsEditingExistingLog(true);
        } catch (error: any) {
          console.error("handleLog - error fetching existing log:", error);
          addNotification({
            type: "error",
            title: "Error",
            message: "Failed to load existing log data. Please try again.",
          });
          return;
        }
      } else {
        // Clear the form for a new log
        setLogEvents("");
        setLogCurrency({
          platinum: "",
          gold: "",
          electrum: "",
          silver: "",
          copper: "",
        });
        setLogItems([]);
        setIsEditingExistingLog(false);
      }

      // Update the sessionLogStatus state to reflect the current status
      setSessionLogStatus((prev) => ({
        ...prev,
        [sessionKey]: hasLogged,
      }));
    } catch (error: any) {
      console.error("handleLog - error checking log status:", error);
      addNotification({
        type: "error",
        title: "Error",
        message: "Failed to check log status. Please try again.",
      });
      return;
    }

    setSelectedSession(session);
    setState("log");
  };

  // Handler: submit log
  const handleSubmitLog = async () => {
    if (!selectedSession || !user?.uid) {
      addNotification({
        type: "error",
        title: "Submission Failed",
        message:
          "Unable to submit log. Please ensure you are logged in and a session is selected.",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Get the character ID for this session from the players array
      const playerInSession = selectedSession.players?.find(
        (p: any) => p.playerId === user.uid
      );

      if (!playerInSession) {
        addNotification({
          type: "error",
          title: "Character Not Found",
          message:
            "Unable to find the character used for this session. Please contact support.",
        });
        return;
      }

      const characterId = playerInSession.characterId;

      // Prepare log data
      const logData = {
        sessionId: selectedSession.id,
        playerId: user.uid,
        characterId: characterId,
        events: logEvents,
        currency: {
          platinum: parseInt(logCurrency.platinum) || 0,
          gold: parseInt(logCurrency.gold) || 0,
          electrum: parseInt(logCurrency.electrum) || 0,
          silver: parseInt(logCurrency.silver) || 0,
          copper: parseInt(logCurrency.copper) || 0,
        },
        items: logItems.filter((item) => item.name && item.quantity),
      };

      // Call appropriate service method based on whether we're editing or creating
      if (isEditingExistingLog) {
        await SessionService.updateSessionLog(logData);
        addNotification({
          type: "success",
          title: "Log Updated",
          message: `Session log for "${selectedSession.name}" has been successfully updated.`,
        });

        // Update session log status to reflect that the log has been updated
        const sessionKey = `${selectedSession.id}_${characterId}`;
        setSessionLogStatus((prev) => ({
          ...prev,
          [sessionKey]: true,
        }));
      } else {
        await SessionService.logSession(logData);

        // Add items to character's inventory (only for new logs)
        const validItems = logItems.filter(
          (item) => item.name && item.quantity
        );
        for (const item of validItems) {
          try {
            await inventory.addItem(characterId, {
              name: item.name,
              type: "item", // Assuming all items are of type "item" for session logging
              rarity: "common", // Assuming all items are common for session logging
              weight: 0,
              value: 0, // Value is not tracked in session logs
              description: item.description,
              quantity: item.quantity,
              magicalProperties: [],
              attunement: item.attunement || false,
              equipped: false,
              source: "session",
            });
          } catch (error) {
            // Failed to add item to inventory - continue with other items
          }
        }

        addNotification({
          type: "success",
          title: "Log Submitted",
          message: `Session log for "${selectedSession.name}" has been successfully submitted. You've earned a memento token! ${validItems.length > 0 ? `Added ${validItems.length} item(s) to your inventory.` : ""}`,
        });

        // Update session log status to reflect that the log has been submitted
        const sessionKey = `${selectedSession.id}_${characterId}`;
        setSessionLogStatus((prev) => ({
          ...prev,
          [sessionKey]: true,
        }));
      }

      // Reset form and return to sessions list
      setState("filled");
      setSelectedSession(null);
      setLogEvents("");
      setLogCurrency({
        platinum: "",
        gold: "",
        electrum: "",
        silver: "",
        copper: "",
      });
      setLogItems([]);
      setIsEditingExistingLog(false);
    } catch (error: any) {
      // Error submitting log - handled by notification system
      const errorMessage =
        error.message || "Failed to submit session log. Please try again.";
      addNotification({
        type: "error",
        title: "Submission Failed",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler: close log
  const handleCancelLog = () => {
    setState("filled");
    setSelectedSession(null);
  };

  // Show loading state
  if (sessionsLoading || titansLoading || usersLoading) {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">Sessions</h2>
        <div style={{textAlign: "center", padding: "2rem"}}>
          Loading sessions...
        </div>
      </div>
    );
  }

  // Show error state
  if (sessionsError) {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">Sessions</h2>
        <div style={{textAlign: "center", padding: "2rem", color: "red"}}>
          Error loading sessions: {sessionsError}
        </div>
      </div>
    );
  }

  // Render empty state
  if (state === "empty") {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">Sessions</h2>
        <section className="env-section">
          <div className="env-sessions-sections">
            <div className="env-card-large env-sessions-card">
              <div className="env-card-title">MY UPCOMING SESSIONS</div>
              <div className="env-empty-state env-empty-in-card">
                Not signed up for any sessions
              </div>
            </div>
            <div className="env-card-large env-sessions-card">
              <div className="env-card-title">AVAILABLE SESSIONS</div>
              <div className="env-empty-state env-empty-in-card">
                No sessions created
              </div>
            </div>
            <div className="env-card-large env-sessions-card">
              <div className="env-card-title">COMPLETED SESSIONS</div>
              <div className="env-empty-state env-empty-in-card">
                No previous sessions available
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Render log state
  if (state === "log" && selectedSession) {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">Sessions</h2>
        <section className="env-section">
          <div className="env-card-large" style={{marginBottom: 24}}>
            <div style={{fontWeight: 600, fontSize: 16, marginBottom: 8}}>
              {selectedSession.name}{" "}
              <span style={{color: "#888", fontWeight: 400}}>
                | {getTitanName(selectedSession)} |{" "}
                {getDisplayDate(selectedSession.date)} at{" "}
                {getDisplayTime(selectedSession.date)} |{" "}
                {selectedSession.location}
              </span>
            </div>
            <div style={{marginBottom: 8}}>
              <RichTextRenderer content={selectedSession.description || ""} />
            </div>
            <div style={{marginBottom: 8, fontSize: 15, color: "#666"}}>
              Attending players
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 16,
                marginBottom: 8,
              }}
            >
              {selectedSession.players.map((p: any, i: number) => (
                <div key={i} style={{minWidth: 120}}>
                  {p.displayName} - {p.characterName} ({p.characterClass}{" "}
                  {p.characterLevel})
                </div>
              ))}
            </div>
          </div>
          <div className="env-card-large">
            <div className="env-section-header">
              LOG [{selectedSession.name}]
            </div>
            <div className="env-form-group">
              <label htmlFor="log-events" className="env-label">
                Key events of the session
              </label>
              <textarea
                id="log-events"
                className="env-textarea"
                placeholder="What major plot points occurred? How did your character develop? What did you learn about the world?"
                value={logEvents}
                onChange={(e) => setLogEvents(e.target.value)}
              />
            </div>
            <div className="env-form-group">
              <label htmlFor="log-currency" className="env-label">
                Currency gained
              </label>
              <div className="env-currency-inputs">
                <input
                  id="platinum"
                  type="number"
                  value={logCurrency.platinum}
                  onChange={(e) =>
                    setLogCurrency((prev) => ({
                      ...prev,
                      platinum: e.target.value,
                    }))
                  }
                  placeholder="0"
                  className="env-input"
                />
                <span>Platinum</span>
                <input
                  id="gold"
                  type="number"
                  value={logCurrency.gold}
                  onChange={(e) =>
                    setLogCurrency((prev) => ({...prev, gold: e.target.value}))
                  }
                  placeholder="0"
                  className="env-input"
                />
                <span>Gold</span>
                <input
                  id="electrum"
                  type="number"
                  value={logCurrency.electrum}
                  onChange={(e) =>
                    setLogCurrency((prev) => ({
                      ...prev,
                      electrum: e.target.value,
                    }))
                  }
                  placeholder="0"
                  className="env-input"
                />
                <span>Electrum</span>
                <input
                  id="silver"
                  type="number"
                  value={logCurrency.silver}
                  onChange={(e) =>
                    setLogCurrency((prev) => ({
                      ...prev,
                      silver: e.target.value,
                    }))
                  }
                  placeholder="0"
                  className="env-input"
                />
                <span>Silver</span>
                <input
                  id="copper"
                  type="number"
                  value={logCurrency.copper}
                  onChange={(e) =>
                    setLogCurrency((prev) => ({
                      ...prev,
                      copper: e.target.value,
                    }))
                  }
                  placeholder="0"
                  className="env-input"
                />
                <span>Copper</span>
              </div>
            </div>
            <div className="env-form-group">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <label className="env-label">Items gained</label>
              </div>
              <InventoryForm
                items={logItems}
                onItemsChange={setLogItems}
                title="Items gained"
              />
            </div>
            <div style={{display: "flex", gap: 8, marginTop: 8}}>
              <button
                className="btn"
                onClick={handleCancelLog}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSubmitLog}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit log"}
              </button>
            </div>
            <div style={{marginTop: 8, fontSize: 13, color: "#888"}}>
              For every session you complete you will receive one memento token
              and 10 downtime days
            </div>
            
            {/* Donation message */}
            <div style={{
              marginTop: 24,
              padding: 16,
              backgroundColor: "#f8f9fa",
              border: "1px solid #e9ecef",
              borderRadius: 8,
              fontSize: 14,
              lineHeight: 1.5
            }}>
              <div style={{marginBottom: 12, color: "#495057"}}>
                The League of Fabled Pursuits is and will always be free to play, for DM's and Players. Because of that, things like the website, and events, are supported by community donations. This is entirely optional, your presence in our community is already hugely supportive, but if you do want and are able to provide a few gold for the upkeep, we'd be extremely grateful.
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <a
                  href="https://www.buymeacoffee.com/fabledpursuits"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "12px 20px",
                    backgroundColor: "#FFD700",
                    color: "#000",
                    textDecoration: "none",
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 500,
                    transition: "background-color 0.2s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#FFC107";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#FFD700";
                  }}
                >
                  <img 
                    src={buymeacoffeeIcon} 
                    alt="Coffee" 
                    style={{height: "20px", width: "20px"}} 
                  />
                  Support The League
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Render filterable sessions list
  if (state === "filled") {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">Sessions</h2>
        <section
          className="env-section"
          style={{height: "calc(100vh - 200px)"}}
        >
          <FilterableList
            data={sortedSessions}
            loading={sessionsLoading}
            error={sessionsError}
            filters={filters}
            onFiltersChange={setFilters}
            onClearFilters={clearFilters}
            searchFields={["name", "description", "location"]}
            searchPlaceholder="Search sessions by name, description, or location..."
            emptyMessage="No sessions found matching your criteria"
            filtersStartCollapsed={true}
            additionalFilter={() => {
              // Additional custom filtering logic if needed
              return true;
            }}
            renderItem={(session) => {
              // Guard clause for user authentication
              if (!user) return null;

              const isExpanded = expandedSessionId === session.id;
              const sessionDate = convertToDate(session.date);
              const now = new Date();
              // For action logic, compare dates without time to include today's sessions
              const nowDate = new Date(
                now.getFullYear(),
                now.getMonth(),
                now.getDate()
              );
              const sessionDateOnly = new Date(
                sessionDate.getFullYear(),
                sessionDate.getMonth(),
                sessionDate.getDate()
              );
              const isInFuture = sessionDateOnly >= nowDate;

              // Helper functions for action logic
              const getActionLabel = () => {
                if (!isInFuture) return undefined; // No actions for past sessions

                if (
                  filters.status === "available" ||
                  (filters.status === "all" && session.status === "available")
                ) {
                  return isSignedUp(session)
                    ? "Withdraw"
                    : isSessionFull(session)
                      ? undefined
                      : "Sign up";
                } else if (
                  filters.status === "upcoming" ||
                  (filters.status === "all" && session.status === "upcoming")
                ) {
                  return isSignedUp(session)
                    ? "Withdraw"
                    : isSessionFull(session)
                      ? undefined
                      : "Sign up";
                }

                return undefined;
              };

              const getActionHandler = () => {
                if (!isInFuture) return undefined; // No actions for past sessions

                if (
                  filters.status === "available" ||
                  (filters.status === "all" && session.status === "available")
                ) {
                  return isSignedUp(session)
                    ? () => handleWithdraw(session)
                    : isSessionFull(session)
                      ? undefined
                      : () => handleSignUp(session);
                } else if (
                  filters.status === "upcoming" ||
                  (filters.status === "all" && session.status === "upcoming")
                ) {
                  return isSignedUp(session)
                    ? () => handleWithdraw(session)
                    : isSessionFull(session)
                      ? undefined
                      : () => handleSignUp(session);
                }

                return undefined;
              };

              const getSecondaryActionLabel = () => {
                // Only show log actions for completed sessions
                const correctFilter =
                  filters.status === "completed" || filters.status === "all";
                const isCompleted =
                  session.status === "completed" || isSessionDone(session.date);
                if (!correctFilter || !isCompleted) return undefined;


                // Check if user participated in this session
                const playerInSession = session.players?.find(
                  (p: any) => p.playerId === user?.uid
                );

                if (!playerInSession) return undefined;

                const characterId = playerInSession.characterId;
                const sessionKey = `${session.id}_${characterId}`;
                const hasLogged = sessionLogStatus[sessionKey];

                // If they haven't logged yet and can log, show "Submit log"
                if (canLogSession(session)) {
                  return "Submit log";
                }

                // If they have already logged, show "Edit log"
                if (hasLogged) {
                  return "Edit log";
                }

                return undefined;
              };

              const getSecondaryActionHandler = () => {
                // Only allow log actions for completed sessions
                const correctFilter =
                  filters.status === "completed" || filters.status === "all";
                const isCompleted =
                  session.status === "completed" || isSessionDone(session.date);
                if (!correctFilter || !isCompleted) return undefined;


                // Check if user participated in this session
                const playerInSession = session.players?.find(
                  (p: any) => p.playerId === user?.uid
                );

                if (!playerInSession) return undefined;

                const characterId = playerInSession.characterId;
                const sessionKey = `${session.id}_${characterId}`;
                const hasLogged = sessionLogStatus[sessionKey];

                // If they can log (haven't logged yet) or have already logged, allow action
                if (canLogSession(session) || hasLogged) {
                  return () => handleLog(session);
                }

                return undefined;
              };

              const handleToggleExpand = () => {
                setExpandedSessionId(
                  expandedSessionId === session.id ? null : session.id || null
                );
              };

              return (
                <div key={session.id}>
                  {/* Compact list item */}
                  <SessionListItem
                    id={session.id || ""}
                    title={session.name}
                    date={getDisplayDate(session.date)}
                    time={getDisplayTime(session.date)}
                    location={session.location}
                    maxPlayers={session.maxPlayers}
                    players={session.players || []}
                    dmName={getDMName(session.dmId)}
                    isExpanded={isExpanded}
                    onToggleExpand={handleToggleExpand}
                    actionLabel={getActionLabel()}
                    onAction={getActionHandler()}
                    actionDisabled={isSubmitting}
                    secondaryActionLabel={getSecondaryActionLabel()}
                    onSecondaryAction={getSecondaryActionHandler()}
                    tertiaryActionLabel="Copy link"
                    onTertiaryAction={() => handleCopyCalendarLink(session.id)}
                  />

                  {/* Expanded session card */}
                  {isExpanded && (
                    <SessionCard
                      id={session.id || ""}
                      title={session.name}
                      date={getDisplayDate(session.date)}
                      time={getDisplayTime(session.date)}
                      duration={session.duration}
                      location={session.location}
                      titan={getTitanName(session)}
                      levelRange={session.levelRange}
                      maxPlayers={session.maxPlayers}
                      players={session.players || []}
                      description={session.description}
                      isOpen={true}
                      actionLabel={getActionLabel()}
                      onAction={getActionHandler()}
                      actionDisabled={isSubmitting}
                      secondaryActionLabel={getSecondaryActionLabel()}
                      onSecondaryAction={getSecondaryActionHandler()}
                      tertiaryActionLabel="Copy link to session"
                      onTertiaryAction={() =>
                        handleCopyCalendarLink(session.id)
                      }
                    />
                  )}
                </div>
              );
            }}
            customActiveFilters={
              <>
                {(
                  <Typography
                    variant="body2"
                    sx={{
                      backgroundColor: "var(--sub)",
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: "0.75rem",
                    }}
                  >
                    Status: {filters.status}
                  </Typography>
                )}
                {filters.titanId && (
                  <Typography
                    variant="body2"
                    sx={{
                      backgroundColor: "var(--sub)",
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: "0.75rem",
                    }}
                  >
                    World:{" "}
                    {titans.find((t) => t.id === filters.titanId)?.name ||
                      filters.titanId}
                  </Typography>
                )}
                {(filters.minLevel !== 1 || filters.maxLevel !== 20) && (
                  <Typography
                    variant="body2"
                    sx={{
                      backgroundColor: "var(--sub)",
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: "0.75rem",
                    }}
                  >
                    Level: {filters.minLevel}-{filters.maxLevel}
                  </Typography>
                )}
              </>
            }
          >
            {/* Advanced filters */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              {/* Status Filter */}
              <FormControl sx={{minWidth: 150}}>
                <InputLabel id="status-filter-label">Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  value={filters.status}
                  label="Status"
                  onChange={(e) =>
                    setFilters({...filters, status: e.target.value as any})
                  }
                >
                  <MenuItem value="all">All Sessions</MenuItem>
                  <MenuItem value="available">Available</MenuItem>
                  <MenuItem value="upcoming">Upcoming</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>

              {/* World Filter */}
              <FormControl sx={{minWidth: 200}}>
                <InputLabel id="titan-filter-label">World</InputLabel>
                <Select
                  labelId="titan-filter-label"
                  value={filters.titanId}
                  label="World"
                  onChange={(e) =>
                    setFilters({...filters, titanId: e.target.value})
                  }
                  disabled={titansLoading}
                >
                  <MenuItem value="">
                    <em>All Worlds</em>
                  </MenuItem>
                  {titans
                    .filter((titan) => titan.isActive)
                    .map((titan) => (
                      <MenuItem key={titan.id} value={titan.id}>
                        {titan.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              {/* Level Range Filters */}
              <Box sx={{display: "flex", gap: 1, alignItems: "center"}}>
                <Typography variant="body2" color="textSecondary">
                  Level Range:
                </Typography>
                <FormControl sx={{minWidth: 80}}>
                  <InputLabel id="min-level-label">Min</InputLabel>
                  <Select
                    labelId="min-level-label"
                    value={filters.minLevel}
                    label="Min"
                    onChange={(e) =>
                      setFilters({...filters, minLevel: Number(e.target.value)})
                    }
                    size="small"
                  >
                    {Array.from({length: 20}, (_, i) => i + 1).map((level) => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="body2" color="textSecondary">
                  to
                </Typography>
                <FormControl sx={{minWidth: 80}}>
                  <InputLabel id="max-level-label">Max</InputLabel>
                  <Select
                    labelId="max-level-label"
                    value={filters.maxLevel}
                    label="Max"
                    onChange={(e) =>
                      setFilters({...filters, maxLevel: Number(e.target.value)})
                    }
                    size="small"
                  >
                    {Array.from({length: 20}, (_, i) => i + 1).map((level) => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </FilterableList>
        </section>

        <Snackbar
          open={copiedOpen}
          autoHideDuration={2500}
          onClose={() => setCopiedOpen(false)}
          anchorOrigin={{vertical: "bottom", horizontal: "center"}}
        >
          <Alert severity="success" onClose={() => setCopiedOpen(false)}>
            Link copied to clipboard
          </Alert>
        </Snackbar>

        {/* Character Selection Modal */}
        <CharacterSelectionModal
          isOpen={showCharacterModal}
          onClose={() => {
            setShowCharacterModal(false);
            setPendingSession(null);
          }}
          onCharacterSelect={handleCharacterSelected}
          session={pendingSession}
        />
      </div>
    );
  }
};

export default Sessions;
